import { FC, ReactNode } from 'react';
import { useAnalyticsFiltersStore } from 'app/modules/analytics/store/analyticsFiltersStore';
import { mergeTailwindClasses } from '@kontentino/ui';
import Card from './Card';
import MetricHeader from './MetricHeader';
import MetricComparisonHeader from './MetricComparisonHeader';
import ChartLegend, { LegendItem } from './charts/components/ChartLegend';
import type { ChangeDirection } from 'api/new-analytics/types';

interface GraphCardProps {
  title: string;
  value: string;
  badge?: number;
  badgeDirection?: ChangeDirection;
  tooltip?: string;
  caption?: string;
  previousValue?: string;
  currentPeriodLabel?: string;
  previousPeriodLabel?: string;
  legend?: LegendItem[];
  children?: ReactNode;
  className?: string;
  contentClassName?: string;
}

const GraphCard: FC<GraphCardProps> = ({
  title,
  value,
  badge,
  badgeDirection,
  tooltip,
  caption,
  previousValue,
  currentPeriodLabel,
  previousPeriodLabel,
  legend,
  children,
  className,
  contentClassName,
}) => {
  const isComparisonEnabled = useAnalyticsFiltersStore(
    (state) => state.isComparisonEnabled,
  );

  const showComparisonHeader =
    isComparisonEnabled && previousValue !== undefined;

  return (
    <Card className={mergeTailwindClasses('tw-gap-6', className)}>
      {showComparisonHeader ? (
        <MetricComparisonHeader
          title={title}
          tooltip={tooltip}
          currentValue={value}
          previousValue={previousValue}
          badge={badge}
          badgeDirection={badgeDirection}
          currentPeriodLabel={currentPeriodLabel}
          previousPeriodLabel={previousPeriodLabel}
        />
      ) : (
        <MetricHeader
          title={title}
          value={value}
          badge={badge}
          badgeDirection={badgeDirection}
          tooltip={tooltip}
          caption={caption}
        />
      )}
      <div
        className={mergeTailwindClasses(
          'tw-mt-auto tw-flex tw-w-full tw-flex-col tw-gap-3',
          contentClassName,
        )}
      >
        <div className="tw-min-h-4 tw-flex tw-items-center">
          {legend && legend.length > 0 ? <ChartLegend items={legend} /> : null}
        </div>
        {children}
      </div>
    </Card>
  );
};

export default GraphCard;
